import { useCallback } from 'react';
import { MRT_Row } from 'mantine-react-table';
import { Claim } from 'interfaces';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { useBulkReconcileTariffs, useUpdateClaim } from 'api/resource';
import { isWriteOffEligibleTariffStatus } from 'utils/claimStatusUtils';
import {
  buildTariffRowId,
  ClaimTableRow,
  getTariffDebits,
} from '../utils/claimTableRows';
import { bulkReconcileTariffs } from './bulkReconcileTariffs';
import { useClaimStatusChangeNotification } from './useClaimStatusChangeNotification';

interface UseClaimTariffActionsParams {
  getClaimById: (claimID: string) => Claim | undefined;
  onActionSuccess?: () => void;
  onTariffsUpdated?: (payload: {
    claimID: string;
    tariffIDs: string[];
    status: string;
    debits?: Claim['debits'];
  }) => void;
  onTariffsReconciled?: (payload: {
    claimID: string;
    tariffIDs: string[];
    recordAdjustment: boolean;
    claim?: Claim;
  }) => void;
}

interface TariffActionPayload {
  claimID: string;
  tariffIDs: string[];
}

interface UpdateTariffStatusPayload extends TariffActionPayload {
  targetStatus: string;
  eligibility?: (status: string) => boolean;
}

const buildTariffRows = (
  claim: Claim,
  tariffIDs: string[]
): ClaimTableRow[] => {
  const targetTariffIDSet = new Set(tariffIDs);

  return (claim.tariffs ?? [])
    .filter((tariff) => targetTariffIDSet.has(tariff.id))
    .map((tariff) => ({
      ...claim,
      rowType: 'tariff' as const,
      rowId: buildTariffRowId(claim.id, tariff.id),
      parentClaimId: claim.id,
      parentClaim: claim,
      tariffId: tariff.id,
      tariffs: [tariff as NonNullable<Claim['tariffs']>[number]],
      debits: getTariffDebits(claim, tariff.id),
    })) as ClaimTableRow[];
};

const getWriteOffAdjustment = (
  requestedAmount: string | number | null | undefined,
  paidAmount: string | number | null | undefined
) => {
  const requested = Number(requestedAmount ?? 0);
  const paid = Number(paidAmount ?? 0);
  return Math.max(requested - paid, 0).toFixed(2);
};

export const useClaimTariffActions = ({
  getClaimById,
  onActionSuccess,
  onTariffsUpdated,
  onTariffsReconciled,
}: UseClaimTariffActionsParams) => {
  const updateClaimMutation = useUpdateClaim();
  const bulkReconcileTariffsMutation = useBulkReconcileTariffs();
  const notifyClaimStatusChange = useClaimStatusChangeNotification();

  const updateClaimTariffsByIds = useCallback(
    async ({
      claimID,
      tariffIDs,
      targetStatus,
      eligibility,
    }: UpdateTariffStatusPayload): Promise<boolean> => {
      const claim = getClaimById(claimID);
      if (
        !claim ||
        !Array.isArray(claim.tariffs) ||
        claim.tariffs.length === 0
      ) {
        return false;
      }

      const targetTariffIDSet = new Set(tariffIDs);
      const nextTariffs = claim.tariffs.map((tariff) => {
        if (
          !targetTariffIDSet.has(tariff.id) ||
          tariff.status === CLAIM_TARIFF_STATUS.PENDING
        )
          return tariff;
        const currentStatus = tariff.status?.trim() ?? '';
        if (eligibility && !eligibility(currentStatus)) return tariff;
        return {
          ...tariff,
          status: targetStatus,
        };
      });

      const hasChanges = nextTariffs.some(
        (tariff, index) => tariff.status !== claim.tariffs[index]?.status
      );
      if (!hasChanges) {
        return false;
      }

      const savedClaim = await updateClaimMutation.mutateAsync({
        dirtyFields: {
          tariffs: true,
        },
        newData: {
          ...claim,
          tariffs: nextTariffs,
        },
      } as any);

      const changedTariffIDs = nextTariffs
        .filter(
          (tariff, index) => tariff.status !== claim.tariffs[index]?.status
        )
        .map((tariff) => tariff.id);
      onTariffsUpdated?.({
        claimID,
        tariffIDs: changedTariffIDs,
        status: targetStatus,
      });
      onActionSuccess?.();
      notifyClaimStatusChange(claim, savedClaim);
      return true;
    },
    [
      getClaimById,
      onActionSuccess,
      onTariffsUpdated,
      updateClaimMutation,
      notifyClaimStatusChange,
    ]
  );

  const markTariffsAs = useCallback(
    (payload: TariffActionPayload & { status: string }) =>
      updateClaimTariffsByIds({
        claimID: payload.claimID,
        tariffIDs: payload.tariffIDs,
        targetStatus: payload.status,
      }),
    [updateClaimTariffsByIds]
  );

  const writeOffTariffs = useCallback(
    async ({ claimID, tariffIDs }: TariffActionPayload): Promise<boolean> => {
      const claim = getClaimById(claimID);
      if (
        !claim ||
        !Array.isArray(claim.tariffs) ||
        claim.tariffs.length === 0
      ) {
        return false;
      }

      const targetTariffIDSet = new Set(tariffIDs);
      const changedTariffIDs = new Set<string>();
      const nextTariffs = claim.tariffs.map((tariff) => {
        if (
          !targetTariffIDSet.has(tariff.id) ||
          tariff.status === CLAIM_TARIFF_STATUS.PENDING
        )
          return tariff;
        if (!isWriteOffEligibleTariffStatus(tariff.status?.trim())) {
          return tariff;
        }

        changedTariffIDs.add(tariff.id);
        return {
          ...tariff,
          status: CLAIM_TARIFF_STATUS.WRITTEN_OFF,
        };
      });

      if (changedTariffIDs.size === 0) {
        return false;
      }

      const nextDebits = (claim.debits ?? []).map((debit) => {
        if (!changedTariffIDs.has(debit.tariffID)) return debit;

        return {
          ...debit,
          adjustment: getWriteOffAdjustment(
            debit.requestedAmount,
            debit.paidAmount
          ),
        };
      });

      const savedClaim = await updateClaimMutation.mutateAsync({
        dirtyFields: {
          tariffs: true,
          debits: true,
        },
        newData: {
          ...claim,
          tariffs: nextTariffs,
          debits: nextDebits,
        },
      } as any);

      onTariffsUpdated?.({
        claimID,
        tariffIDs: Array.from(changedTariffIDs),
        status: CLAIM_TARIFF_STATUS.WRITTEN_OFF,
        debits: nextDebits,
      });
      onActionSuccess?.();
      notifyClaimStatusChange(claim, savedClaim);
      return true;
    },
    [
      getClaimById,
      onActionSuccess,
      onTariffsUpdated,
      updateClaimMutation,
      notifyClaimStatusChange,
    ]
  );

  const resubmitTariffs = useCallback(
    (payload: TariffActionPayload) =>
      updateClaimTariffsByIds({
        ...payload,
        targetStatus: CLAIM_TARIFF_STATUS.READY,
        eligibility: (status) =>
          status === CLAIM_TARIFF_STATUS.REJECTED ||
          status === CLAIM_TARIFF_STATUS.UNDERPAID ||
          status === CLAIM_TARIFF_STATUS.OVERPAID,
      }),
    [updateClaimTariffsByIds]
  );

  const reconcileTariffs = useCallback(
    async ({
      claimID,
      tariffIDs,
      recordAdjustment = false,
    }: TariffActionPayload & { recordAdjustment?: boolean }) => {
      const claim = getClaimById(claimID);
      if (!claim || tariffIDs.length === 0) return false;

      const rows = buildTariffRows(claim, tariffIDs);
      if (rows.length === 0) return false;

      const { reconciledClaims, claims } = await bulkReconcileTariffs({
        rows: rows.map(
          (row) =>
            ({
              original: row,
            } as MRT_Row<ClaimTableRow>)
        ),
        bulkReconcileTariffsMutation,
        recordAdjustment,
      });

      if (reconciledClaims <= 0) return false;

      const savedClaim = claims?.find(
        (updatedClaim) => updatedClaim.id === claimID
      );
      onTariffsReconciled?.({
        claimID,
        tariffIDs,
        recordAdjustment,
        claim: savedClaim,
      });
      onActionSuccess?.();
      notifyClaimStatusChange(claim, savedClaim);
      return true;
    },
    [
      bulkReconcileTariffsMutation,
      getClaimById,
      onActionSuccess,
      onTariffsReconciled,
      notifyClaimStatusChange,
    ]
  );

  return {
    markTariffsAs,
    reconcileTariffs,
    resubmitTariffs,
    updateClaimTariffsByIds,
    writeOffTariffs,
  };
};
